<template>
  <TeleportComponent
    v-if="isMounted && (dietType === 'patient' || dietSaveStatus !== 'initialized')"
    to="#app-content-header-portal"
  >
    <div class="diet-detail-header">
      <SavingStatus :status="dietSaveStatus" type="diet" />

      <template v-if="dietType === 'patient'">
        <DietNavigationButtons
          :next-diet-id="nextDietId"
          :previous-diet-id="previousDietId"
          @select="emit('select', $event)"
        />
        <ChangeActiveDiet
          :diet-id="dietId"
          :patient-id="patientId"
          @select="emit('select', $event)"
        />
        <div class="separator" />
        <ChangeDietDates
          :diet-id="dietId"
          :start-date="dietStartDate"
          :end-date="dietEndDate"
          :diet-status="dietStatus"
          @refresh="emit('refresh')"
        />
        <ChangeDietStatus :diet-id="dietId" :diet-status="dietStatus" @refresh="emit('refresh')" />
      </template>

      <ShareDietButton
        v-if="patientId && !isSharedWithPatient"
        :patient-id="patientId"
        :disabled="dietStatus === 3"
        :disabled-tooltip="dietStatus === 3 ? 'Zduplikuj ten jadłospis, aby móc edytować go jako Szkic' : undefined"
        @click="shareModalOpen = true"
      />
      <StyledButton
        v-if="isSharedWithPatient"
        class="diet-detail-header__undo-share"
        variant="outline"
        @click="undoShareModalOpen = true"
      >
        Cofnij udostępnienie
      </StyledButton>
    </div>
  </TeleportComponent>

  <ShareDietWithPatientModal
    v-if="shareModalOpen && patientId"
    :days-count="dietDaysCount"
    :diet-id="dietId"
    :patient-id="patientId"
    :start-date="diet?.data?.start_date"
    @close="shareModalOpen = false"
    @success="handleShareSuccess"
  />
  <UndoShareDietModal
    v-if="undoShareModalOpen && patientId"
    :diet-id="dietId"
    :patient-id="patientId"
    @close="undoShareModalOpen = false"
    @success="handleUndoShareSuccess"
  />
</template>

<script lang="ts" setup>
  import { addDays, parse } from 'date-fns'
  import { storeToRefs } from 'pinia'
  import { computed, ref, Teleport as TeleportComponent } from 'vue'

  import ShareDietWithPatientModal from '@/components/diets/ShareDietWithPatientModal.vue'
  import UndoShareDietModal from '@/components/diets/UndoShareDietModal.vue'
  import SavingStatus from '@/components/SavingStatus.vue'
  import { StyledButton } from '@/components/StyledButton'
  import { useDietsStore } from '@/store/dietsStore'
  import { getDietDaysCount } from '@/utils/dietDays'

  import type { DietType } from '@/store/types'

  import { ChangeActiveDiet } from '../../../components/ChangeActiveDiet'
  import { ChangeDietDates } from '../../../components/ChangeDietDates'
  import { ChangeDietStatus } from '../../../components/ChangeDietStatus'
  import { DietNavigationButtons } from '../../../components/DietNavigationButtons'
  import { ShareDietButton } from '../../../components/ShareDietButton'

  const props = defineProps<{
    dietId: number
    dietType: DietType
    isGenerating: boolean
    isMounted: boolean
  }>()
  const emit = defineEmits<{
    refresh: []
    select: [dietId: number]
  }>()

  const { diet, dietSaveStatus, dietStatus, inApp } = storeToRefs(useDietsStore())
  const shareModalOpen = ref(false)
  const undoShareModalOpen = ref(false)
  const patientId = computed(() =>
    diet.value && 'dietitian_patient' in diet.value ? diet.value.dietitian_patient.id : undefined,
  )
  const previousDietId = computed(() =>
    diet.value && 'prev_diet' in diet.value ? diet.value.prev_diet : null,
  )
  const nextDietId = computed(() =>
    diet.value && 'next_diet' in diet.value ? diet.value.next_diet : null,
  )
  const dietStartDate = computed(() => {
    const startDate = diet.value?.data.start_date
    return startDate?.includes('-') ? parse(startDate, 'dd-MM-yyyy', new Date()) : null
  })
  const dietDaysCount = computed(() => getDietDaysCount(diet.value))
  const dietEndDate = computed(() =>
    dietStartDate.value ? addDays(dietStartDate.value, dietDaysCount.value - 1) : null,
  )
  const isSharedWithPatient = computed(() => props.dietType === 'patient' && inApp.value)

  function handleShareSuccess() {
    emit('refresh')
    shareModalOpen.value = false
  }

  function handleUndoShareSuccess() {
    emit('refresh')
    undoShareModalOpen.value = false
  }
</script>

<style scoped>
  .diet-detail-header {
    display: flex;
    flex-grow: 1;
    flex-direction: row;
    gap: 10px;
    align-items: center;
    justify-content: flex-end;
    min-width: 300px;
  }

  .diet-detail-header__undo-share {
    margin-right: 12px;
  }

  .separator {
    width: 1px;
    height: 100%;
    background: var(--color-tertiary-text);
  }
</style>
